import { useMutation, useQuery } from "@tanstack/react-query"
import { Checkbox, Space, Table, Button, message } from "antd"
import type React from "react"
import { memo, useEffect, useState } from "react"
import { DragModal } from "@/components/Modal"
import { roleService } from "@/services/role/roleApi"

interface PermissionModalProps {
  open: boolean
  roleId: number | string | null
  roleName: string
  onCancel: () => void
}

const MODULES = [
  { key: "dashboard", label: "Bảng điều khiển (Dashboard)" },
  { key: "DANH_SACH_TAI_KHOAN_ADMIN", label: "Quản lý tài khoản Admin" },
  { key: "QUAN_LY_ROLE", label: "Quản lý Role / Vai trò" },
  { key: "QL_TU_GOC", label: "Quản lý Từ gốc" },
]

const PERMISSIONS = [
  { key: "ACCESS", label: "Truy cập" },
  { key: "CREATE", label: "Thêm mới" },
  { key: "UPDATE", label: "Cập nhật" },
  { key: "DELETE", label: "Xóa" },
  { key: "APPROVE", label: "Phê duyệt" },
  { key: "IMPORT_EXPORT_EXCEL", label: "Xuất/Nhập Excel" },
]

/**
 * Component Modal phân quyền vai trò (PermissionModal)
 */
const PermissionModal: React.FC<PermissionModalProps> = memo(({ open, roleId, roleName, onCancel }) => {
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  // Lấy danh sách quyền hiện tại của vai trò
  const { data: permissionsResult, isFetching } = useQuery({
    queryKey: ["role_permissions", roleId],
    queryFn: () => roleService.getPermissions(roleId!),
    enabled: !!roleId && open,
  })

  // Cập nhật selectedPermissions khi API trả về kết quả
  useEffect(() => {
    if (permissionsResult?.data) {
      const activeKeys = permissionsResult.data.map((item: any) => `${item.permission}-${item.module}`)
      setSelectedPermissions(activeKeys)
    } else {
      setSelectedPermissions([])
    }
  }, [permissionsResult])

  const assignMutation = useMutation({
    mutationFn: (permissions: string[]) =>
      roleService.assignPermission({
        role_id: roleId!,
        permissions,
      }),
    onSuccess: () => {
      message.success("Cập nhật phân quyền thành công!")
      onCancel()
    },
    onError: () => {
      message.error("Có lỗi xảy ra khi phân quyền!")
    },
  })

  const handleCheckboxChange = (permissionKey: string, moduleKey: string, checked: boolean) => {
    const key = `${permissionKey}-${moduleKey}`
    if (checked) {
      setSelectedPermissions((prev) => [...prev, key])
    } else {
      setSelectedPermissions((prev) => prev.filter((item) => item !== key))
    }
  }

  const handleSelectAllModule = (moduleKey: string, checked: boolean) => {
    const moduleKeys = PERMISSIONS.map((p) => `${p.key}-${moduleKey}`)
    if (checked) {
      setSelectedPermissions((prev) => Array.from(new Set([...prev, ...moduleKeys])))
    } else {
      setSelectedPermissions((prev) => prev.filter((item) => !moduleKeys.includes(item)))
    }
  }

  const handleSave = () => {
    assignMutation.mutate(selectedPermissions)
  }

  const columns = [
    {
      title: "Chức năng / Module",
      dataIndex: "label",
      key: "label",
      width: 250,
      render: (text: string, record: any) => {
        const moduleKeys = PERMISSIONS.map((p) => `${p.key}-${record.key}`)
        const isAllChecked = moduleKeys.every((key) => selectedPermissions.includes(key))
        const isSomeChecked = moduleKeys.some((key) => selectedPermissions.includes(key)) && !isAllChecked

        return (
          <Space>
            <Checkbox
              checked={isAllChecked}
              indeterminate={isSomeChecked}
              onChange={(e) => handleSelectAllModule(record.key, e.target.checked)}
            />
            <span className="font-medium">{text}</span>
          </Space>
        )
      },
    },
    ...PERMISSIONS.map((p) => ({
      title: p.label,
      key: p.key,
      align: "center" as const,
      width: 110,
      render: (_: any, record: any) => {
        const key = `${p.key}-${record.key}`
        const checked = selectedPermissions.includes(key)
        return (
          <Checkbox checked={checked} onChange={(e) => handleCheckboxChange(p.key, record.key, e.target.checked)} />
        )
      },
    })),
  ]

  return (
    <DragModal
      centered
      title={`Phân quyền vai trò: ${roleName}`}
      open={open}
      onCancel={onCancel}
      width={1000}
      destroyOnHidden={true}
      footer={
        <Space>
          <Button onClick={onCancel}>Hủy</Button>
          <Button type="primary" onClick={handleSave} loading={assignMutation.isPending}>
            Lưu thay đổi
          </Button>
        </Space>
      }
    >
      <Table
        bordered
        dataSource={MODULES}
        columns={columns}
        rowKey="key"
        pagination={false}
        loading={isFetching}
        size="small"
      />
    </DragModal>
  )
})

PermissionModal.displayName = "PermissionModal"

export default PermissionModal
